/**
 * Planned work types + per-type quantities for pipeline sections (SOW).
 * API: `plannedWorkItems`: [{ workType, quantity }]; legacy `plannedWorks` / `plannedWelds` kept for roll-ups.
 */

export const PLANNED_WORK_TYPE_OPTIONS = [
  { value: 'Components', label: 'Components' },
  { value: 'Days', label: 'Days' },
  { value: 'Field Joints', label: 'Field Joints' },
  { value: 'Welds', label: 'Welds' },
  { value: 'Coating', label: 'Coating' },
  { value: 'Weld', label: 'Weld' },
];

const WELD_LIKE_FOR_ROLLUP = new Set(['Welds', 'Weld', 'Field Joints']);

/** Canonical label for a planned work type (case-insensitive match to PLANNED_WORK_TYPE_OPTIONS). */
export function normalizePlannedWorkType(workType) {
  const s = String(workType || '').trim();
  if (!s) return '';
  const match = PLANNED_WORK_TYPE_OPTIONS.find(
    (o) => o.value.toLowerCase() === s.toLowerCase()
  );
  return match ? match.value : s;
}

function toNullableNumber(v) {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v).trim());
  return Number.isFinite(n) ? n : null;
}

/** UI rows: { id, workType, quantity } */
export function parsePlannedWorkItemsFromRecord(record) {
  if (!record) return [];
  const raw = record?.plannedWorkItems ?? record?.planned_work_items;
  if (Array.isArray(raw) && raw.length) {
    const parsed = raw
      .map((it, idx) => ({
        id: `pwi-${idx}-${it.workType ?? it.work_type ?? idx}`,
        workType: String(it.workType ?? it.work_type ?? '').trim(),
        quantity:
          it.quantity != null && it.quantity !== '' ? String(it.quantity) : '',
      }))
      .filter((x) => x.workType);
    if (parsed.length) return parsed;
  }
  const legacyWork = record?.plannedWorks ?? record?.planned_works ?? '';
  const legacyQty = record?.plannedWelds ?? record?.planned_welds;
  if (legacyWork != null && String(legacyWork).trim() !== '') {
    return [
      {
        id: 'legacy',
        workType: String(legacyWork).trim(),
        quantity:
          legacyQty != null && legacyQty !== '' ? String(legacyQty) : '',
      },
    ];
  }
  if (legacyQty != null && legacyQty !== '') {
    const q = toNullableNumber(legacyQty);
    if (q != null) {
      return [{ id: 'legacy-welds', workType: 'Welds', quantity: String(legacyQty) }];
    }
  }
  return [];
}

export function formatPlannedWorkSummary(recordOrItems) {
  const items = Array.isArray(recordOrItems)
    ? recordOrItems
    : parsePlannedWorkItemsFromRecord(recordOrItems);
  if (!items.length) return '—';
  return items
    .map((it) => {
      const wt = it.workType ?? '';
      const q =
        it.quantity != null && String(it.quantity).trim() !== ''
          ? String(it.quantity).trim()
          : '—';
      return `${wt}: ${q}`;
    })
    .join('; ');
}

export function rollupWeldsQuantityFromItems(items) {
  let sum = 0;
  let any = false;
  (Array.isArray(items) ? items : []).forEach((it) => {
    const wt = normalizePlannedWorkType(it.workType ?? it.work_type);
    if (!WELD_LIKE_FOR_ROLLUP.has(wt)) return;
    const q = toNullableNumber(it.quantity ?? it.qty);
    if (q != null) {
      sum += q;
      any = true;
    }
  });
  return any ? sum : null;
}

/** Single-cell rollup for tables (weld-like quantities only). */
export function displayPlannedQtySummary(record) {
  const items = parsePlannedWorkItemsFromRecord(record);
  if (items.length > 0) {
    const rolled = rollupWeldsQuantityFromItems(items);
    if (rolled != null) return String(rolled);
    return '—';
  }
  if (record?.plannedWelds != null && record?.plannedWelds !== '')
    return String(record.plannedWelds);
  return '—';
}

/** Per-mainline planned work chips (Welds, Coating, etc.) for Details table rows. */
export function getPlannedWorkChipsForRecord(record) {
  if (!record) return [];
  const totals = new Map();
  parsePlannedWorkItemsFromRecord(record).forEach((it) => {
    const wt = normalizePlannedWorkType(it.workType);
    if (!wt) return;
    const q = toNullableNumber(it.quantity);
    if (q == null || q <= 0) return;
    totals.set(wt, (totals.get(wt) || 0) + q);
  });
  const welds = getPlannedWeldsTotalForRecord(record);
  if (welds != null && welds > 0 && !totals.has('Welds') && !totals.has('Weld')) {
    totals.set('Welds', welds);
  }
  const coating = getPlannedCoatingQuantity(record);
  if (coating != null && coating > 0) {
    totals.set('Coating', coating);
  }
  if (!totals.size) return [];
  return [...totals.entries()]
    .map(([workType, quantity]) => ({ workType, quantity }))
    .sort((a, b) =>
      a.workType.localeCompare(b.workType, undefined, { sensitivity: 'base' })
    );
}

/** Display unit per planned work type (pipeline header chips). */
export const PLANNED_WORK_DISPLAY_UNITS = {
  Components: 'ea',
  Days: 'days',
  'Field Joints': 'joints',
  Welds: 'welds',
  Weld: 'welds',
  Coating: 'm',
};

export function plannedWorkTypeUnit(workType) {
  const wt = normalizePlannedWorkType(workType);
  return PLANNED_WORK_DISPLAY_UNITS[wt] || '';
}

/** Label + value for Details / DSR metric chips (e.g. Planned Welds → "1321 welds"). */
export function formatPlannedWorkChipParts(workType, quantity) {
  const wt = normalizePlannedWorkType(workType) || String(workType || '').trim();
  const unit = plannedWorkTypeUnit(wt);
  const n = typeof quantity === 'number' ? quantity : parseFloat(String(quantity));
  const qtyDisplay = Number.isFinite(n)
    ? Number.isInteger(n)
      ? String(n)
      : n.toLocaleString(undefined, { maximumFractionDigits: 3 })
    : String(quantity ?? '');
  return {
    label: `Planned ${wt}`,
    value: unit ? `${qtyDisplay} ${unit}` : qtyDisplay,
  };
}

export function getPlannedWeldsTotalForRecord(record) {
  const items = parsePlannedWorkItemsFromRecord(record);
  const rolled = rollupWeldsQuantityFromItems(items);
  if (rolled != null) return rolled;
  return toNullableNumber(record?.plannedWelds ?? record?.planned_welds);
}

const COATING_PLANNED_WORK_TYPE = 'coating';

function isCoatingPlannedWorkType(workType) {
  return String(workType || '').trim().toLowerCase() === COATING_PLANNED_WORK_TYPE;
}

/**
 * Quantity for one planned work type from `plannedWorkItems` only (no legacy weld totals).
 * When several rows share the same type, sums only that type — never all work types.
 */
export function getPlannedWorkQuantityByType(record, workType, options = {}) {
  const { plannedWorkItemsOnly = false } = options;
  if (!record || !workType) return null;
  const target = String(workType).trim().toLowerCase();

  const raw = record?.plannedWorkItems ?? record?.planned_work_items;
  if (Array.isArray(raw) && raw.length > 0) {
    let sum = 0;
    let any = false;
    raw.forEach((it) => {
      const wt = String(it.workType ?? it.work_type ?? '').trim().toLowerCase();
      if (wt !== target) return;
      const q = toNullableNumber(it.quantity ?? it.qty);
      if (q != null) {
        sum += q;
        any = true;
      }
    });
    return any ? sum : null;
  }

  if (plannedWorkItemsOnly) return null;

  const items = parsePlannedWorkItemsFromRecord(record);
  for (const it of items) {
    if (String(it.workType || '').trim().toLowerCase() === target) {
      return toNullableNumber(it.quantity);
    }
  }
  return null;
}

/** Coating line only — not weld rollup, not plannedWelds total, not other work types. */
export function getPlannedCoatingQuantity(record) {
  const raw = record?.plannedWorkItems ?? record?.planned_work_items;
  if (!Array.isArray(raw) || raw.length === 0) return null;

  let sum = 0;
  let any = false;
  raw.forEach((it) => {
    if (!isCoatingPlannedWorkType(it.workType ?? it.work_type)) return;
    const q = toNullableNumber(it.quantity ?? it.qty);
    if (q != null) {
      sum += q;
      any = true;
    }
  });
  return any ? sum : null;
}

/** Display string for Coating planned work (Details / pipeline mainline). */
export function formatPlannedCoatingScopeDisplay(record) {
  const q = getPlannedCoatingQuantity(record);
  if (q == null) return '—';
  const unit = plannedWorkTypeUnit('Coating');
  return unit ? `${q} ${unit}` : String(q);
}

/** Dev: trace why DSR shows a given planned coating value (filter console: DSR-PJ-Coating). */
export function logMainlinePlannedCoatingDebug(record, contextLabel = 'mainline') {
  const tag = '[DSR-PJ-Coating]';
  const raw = record?.plannedWorkItems ?? record?.planned_work_items;
  const coatingRows = Array.isArray(raw)
    ? raw.filter((it) => isCoatingPlannedWorkType(it.workType ?? it.work_type))
    : [];

  console.group(`${tag} ${contextLabel}`);
  console.log(`${tag} mainline summary`, {
    id: record?.id,
    sowId: record?.sowId,
    pipelineSection: record?.pipelineSection ?? record?.pipeline_section,
    pipeStringId: record?.pipeStringId ?? record?.pipe_string_id,
    plannedWelds: record?.plannedWelds ?? record?.planned_welds,
    plannedWorks: record?.plannedWorks ?? record?.planned_works,
  });
  console.log(`${tag} plannedWorkItems (all rows)`, raw);
  if (Array.isArray(raw)) {
    raw.forEach((it, index) => {
      const wt = it.workType ?? it.work_type;
      console.log(`${tag} plannedWorkItems[${index}]`, {
        workType: wt,
        quantity: it.quantity ?? it.qty,
        isCoatingRow: isCoatingPlannedWorkType(wt),
      });
    });
  }
  console.log(`${tag} coating rows only`, coatingRows);
  console.log(`${tag} getPlannedCoatingQuantity (sum of Coating rows only)`, getPlannedCoatingQuantity(record));
  console.log(`${tag} display label`, formatPlannedCoatingScopeDisplay(record));
  if (coatingRows.length > 1) {
    console.warn(
      `${tag} multiple Coating lines on this mainline — quantities are summed:`,
      coatingRows.map((it) => it.quantity ?? it.qty).join(' + ')
    );
  }
  console.groupEnd();
}

/** Sum weld-like planned quantities across all pipeline (SOW) rows. */
export function sumPlannedWeldsAcrossRecords(records) {
  if (!Array.isArray(records)) return null;
  let sum = 0;
  let any = false;
  records.forEach((r) => {
    const q = getPlannedWeldsTotalForRecord(r);
    if (q != null) {
      sum += q;
      any = true;
    }
  });
  return any ? sum : null;
}

/** Per work-type quantity totals across pipelines (for summary chips). */
export function aggregatePlannedWorkQuantitiesByType(records) {
  if (!Array.isArray(records)) return [];
  const totals = new Map();
  records.forEach((r) => {
    parsePlannedWorkItemsFromRecord(r).forEach((it) => {
      const wt = normalizePlannedWorkType(it.workType);
      if (!wt) return;
      const q = toNullableNumber(it.quantity);
      if (q == null) return;
      totals.set(wt, (totals.get(wt) || 0) + q);
    });
  });
  return [...totals.entries()]
    .map(([workType, quantity]) => ({ workType, quantity }))
    .sort((a, b) => a.workType.localeCompare(b.workType, undefined, { sensitivity: 'base' }));
}

/** Project-level planned work chips (header) — same logic as per-mainline chips, summed. */
export function aggregatePlannedWorkChipsForProject(records) {
  if (!Array.isArray(records)) return [];
  const totals = new Map();
  records.forEach((r) => {
    getPlannedWorkChipsForRecord(r).forEach(({ workType, quantity }) => {
      totals.set(workType, (totals.get(workType) || 0) + quantity);
    });
  });
  return [...totals.entries()]
    .map(([workType, quantity]) => ({ workType, quantity }))
    .filter(({ quantity }) => quantity > 0)
    .sort((a, b) =>
      a.workType.localeCompare(b.workType, undefined, { sensitivity: 'base' })
    );
}

/** Fields to merge into add/update SOW payload. */
export function buildPlannedWorkApiFields(uiRows) {
  const cleaned = (Array.isArray(uiRows) ? uiRows : [])
    .filter((it) => it.workType && String(it.workType).trim())
    .map((it) => ({
      workType: String(it.workType).trim(),
      quantity: toNullableNumber(it.quantity),
    }));
  const rollup = rollupWeldsQuantityFromItems(cleaned);
  const legacyWorks = cleaned.map((c) => c.workType).filter(Boolean).join(', ');
  return {
    ...(cleaned.length ? { plannedWorkItems: cleaned } : {}),
    ...(legacyWorks ? { plannedWorks: legacyWorks } : {}),
    ...(rollup != null ? { plannedWelds: rollup } : {}),
  };
}
